import { CouponApi } from '@/apis';
import { Campain, Coupon, CouponModel } from '@/types/coupon';
import { ResponseData } from '@/types/http';
import {
    CreateCampainRequest,
    CreateCouponReq,
    CreateCouponRequest,
    GetCouponsRequest,
    GetCouponsResponse,
    UpdateCampainRequest,
    UpdateCouponReq
} from '@/types/http-payload/coupon';
import { TableState } from '@tanstack/react-table';
import { action, flow, makeObservable, observable } from 'mobx';
import RootStore from '.';
import BaseStore from './BaseStore';
import { COUPON_TYPE } from '@/types/enums';

export default class CouponStore extends BaseStore {
    campainList: Campain[] = [];
    campainDetail: Campain | null = null;
    couponList: Coupon[] = [];
    couponDetail: Coupon | null = null;
    couponUsed: number = 0;
    pagingCoupon: TableState = this.defaultPaging;

    // coupon cms
    coupon: CouponModel = {} as CouponModel;
    couponType: number = COUPON_TYPE.FOREVER_USE;

    foreverCoupons: CouponModel[] = [];
    oneTimeCoupons: CouponModel[] = [];

    totalRecordForever: number = 0;
    totalRecordOneTime: number = 0;

    defaultPagingForever: TableState = this.defaultPaging;
    defaultPagingOneTime: TableState = this.defaultPaging;

    pagingForever: TableState = this.defaultPagingForever;
    pagingOneTime: TableState = this.defaultPagingOneTime;
    // End: coupon cms

    api: CouponApi;

    constructor(rootStore: RootStore) {
        super(rootStore);
        makeObservable(this, {
            // cms
            coupon: observable,
            couponType: observable,
            foreverCoupons: observable,
            oneTimeCoupons: observable,
            totalRecordForever: observable,
            totalRecordOneTime: observable,
            pagingForever: observable,
            pagingOneTime: observable,
            getCouponsCMS: flow.bound,
            getCouponDetail: flow.bound,
            createCoupon: flow.bound,
            updateCoupon: flow.bound,
            deleteCoupon: flow.bound,
            cleanForeverCouponCMS: action.bound,
            cleanOneTimeCouponCMS: action.bound,
            //
            couponList: observable,
            couponDetail: observable,
            campainList: observable,
            campainDetail: observable,
            couponUsed: observable,
            pagingCoupon: observable,
            deleteCampain: flow.bound,
            updateCampain: flow.bound,
            getCampainDetail: flow.bound,
            createCampain: flow.bound,
            createCoupons: flow.bound,
            getCouponList: flow.bound,
            deleteCoupons: flow.bound,
            exportCouponsList: flow.bound
        });
        this.api = new CouponApi();
    }

    // #region CMS
    *getCouponsCMS(searchParams?: GetCouponsRequest, paging?: TableState) {
        try {
            const payload = {
                ...this.convertPagingFromTableToRequest(paging ?? this.paging),
                ...searchParams
            };
            const res: ResponseData<GetCouponsResponse> =
                yield this.rootStore.apiStore.call(
                    this.api,
                    this.api.getCouponsCMS,
                    payload
                );

            if (!res.ok) {
                return false;
            }

            if (searchParams?.couponType === COUPON_TYPE.FOREVER_USE) {
                this.foreverCoupons = res.data.elements;
                this.pagingForever = {
                    ...this.pagingForever,
                    sorting: paging?.sorting ?? [],
                    pagination: this.convertPaginationFromRequestToTable(
                        res.data.paginate
                    )
                };
                this.totalRecordForever = res.data.paginate.totalRecord;
                return true;
            } else if (searchParams?.couponType === COUPON_TYPE.ONE_TIME_USE) {
                this.oneTimeCoupons = res.data.elements;
                this.pagingOneTime = {
                    ...this.pagingOneTime,
                    sorting: paging?.sorting ?? [],
                    pagination: this.convertPaginationFromRequestToTable(
                        res.data.paginate
                    )
                };
                this.totalRecordOneTime = res.data.paginate.totalRecord;
                return true;
            }

            return false;
        } catch (error) {
            return false;
        }
    }

    *getCouponDetail(id: number) {
        try {
            const res: ResponseData<any> = yield this.rootStore.apiStore.call(
                this.api,
                this.api.getCouponDetail,
                id
            );
            if (res?.ok) {
                this.coupon = res?.data;
                return res?.data;
            }
            return false;
        } catch (error) {
            return false;
        }
    }

    *createCoupon(payload: CreateCouponReq) {
        try {
            const res: ResponseData<any> = yield this.rootStore.apiStore.call(
                this.api,
                this.api.createCoupon,
                payload
            );
            if (res?.ok) {
                this.coupon = res?.data;
                return true;
            }
            return false;
        } catch (error) {
            return false;
        }
    }

    *updateCoupon(payload: UpdateCouponReq) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(
                this.api,
                this.api.updateCoupon,
                payload
            );
            if (res?.ok) {
                return true;
            }
            return false;
        } catch (error) {
            return false;
        }
    }

    *deleteCoupon(id: number) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(
                this.api,
                this.api.deleteCoupon,
                id
            );
            if (res?.ok) {
                return true;
            }
            return false;
        } catch (error) {
            return false;
        }
    }
    // #endregion CMS

    *deleteCampain(id: number) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(
                this.api,
                this.api.deleteCampain,
                id
            );
            if (res?.ok) {
                return true;
            }
            return false;
        } catch (error) {
            return false;
        }
    }

    *updateCampain(payload: UpdateCampainRequest) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(
                this.api,
                this.api.updateCampain,
                payload
            );
            if (res?.ok) {
                return true;
            }
            return false;
        } catch (error) {
            return false;
        }
    }

    *getCampainDetail(id: number) {
        try {
            const res: ResponseData<any> = yield this.rootStore.apiStore.call(
                this.api,
                this.api.getCampainDetail,
                id
            );
            if (res?.ok) {
                this.campainDetail = res?.data;
                return true;
            }
            return false;
        } catch (error) {
            return false;
        }
    }

    *createCampain(payload: CreateCampainRequest) {
        try {
            const res: ResponseData<any> = yield this.rootStore.apiStore.call(
                this.api,
                this.api.createCampain,
                payload
            );
            if (res?.ok) {
                this.campainDetail = res?.data;
                return true;
            }
            return false;
        } catch (error) {
            return false;
        }
    }

    *createCoupons(payload: CreateCouponRequest) {
        try {
            const res: ResponseData<any> = yield this.rootStore.apiStore.call(
                this.api,
                this.api.createCoupons,
                payload
            );
            if (res?.ok) {
                return true;
            }
            return false;
        } catch (error) {
            return false;
        }
    }

    *getCouponList(campainId: number, paging?: TableState) {
        try {
            const payload = {
                ...this.convertPagingFromTableToRequest(paging ?? this.pagingCoupon),
                id: campainId
            };
            const res: ResponseData<any> = yield this.rootStore.apiStore.call(
                this.api,
                this.api.getCouponList,
                payload
            );
            if (res.ok) {
                this.couponList = res?.data?.elements;
                this.couponUsed = res?.data?.couponUsed;
                this.pagingCoupon = {
                    ...this.pagingCoupon,
                    sorting: paging?.sorting ?? [],
                    pagination: this.convertPaginationFromRequestToTable(
                        res.data.paginate
                    )
                };
                this.totalRecord = res?.data?.paginate?.totalRecord;
            }
        } catch (error) { }
    }

    *deleteCoupons(campainId: number, couponIds: number[]) {
        try {
            const payload = { id: campainId, couponIds };
            const res: ResponseData<any> = yield this.rootStore.apiStore.call(
                this.api,
                this.api.deleteCoupons,
                payload
            );
            if (res?.ok) {
                return true;
            }
            return false;
        } catch (error) {
            return false;
        }
    }

    *exportCouponsList(campainId: number) {
        try {
            const res = yield this.rootStore.apiStore.call(
                this.api,
                this.api.exportCouponsList,
                campainId
            );
            if (res) {
                return true;
            }
            return false;
        } catch (error) {
            return false;
        }
    }
    // #endregion

    clean(): void {
        super.clean();

        this.campainList = [];
        this.campainDetail = null;
        this.couponList = [];
        this.couponUsed = 0;

        this.pagingCoupon = this.defaultPaging;
    }

    cleanForeverCouponCMS(): void {
        super.clean();

        this.foreverCoupons = [];
        this.totalRecordForever = 0;
        this.pagingForever = this.defaultPagingForever;
    }

    cleanOneTimeCouponCMS(): void {
        super.clean();

        this.oneTimeCoupons = [];
        this.totalRecordOneTime = 0;
        this.pagingOneTime = this.defaultPagingOneTime;
    }
}
